<template>
  <div class="mce-gradient-content">
    <div class="mce-gradient-presets" ref="presetContainer">
      <div
        v-for="(tmpGradientModel, tmpPresetKey) in presets"
        :key="tmpPresetKey"
        :class="{
          'wt-highlighted': _.isEqual(gradientModel, tmpGradientModel),
        }"
        @click="applyPreset(tmpGradientModel)"
        :style="{ background: gradientModelToCss(tmpGradientModel) }"
        ref="presetControl"
        class="mce-gradient-preset"
      ></div>
    </div>
    <div class="mce-gradient-title">Options</div>
    <div class="mce-gradient-rows">
      <div>
        <div class="mce-gradient-row-title">Type</div>
        <div class="mce-gradient-row-content">
          <div
            class="mce-gradient-type-dropdown"
            v-click-outside="typesDropdownClickedOutside"
            :class="{ opened: showTypesDropdown }"
          >
            <span @click="showTypesDropdown = !showTypesDropdown">
              <div
                class="mce-gradient-type-preview"
                v-bind:style="{ background: currentGradientType.previewStyle }"
              ></div>
              {{ currentGradientType.title }}
              <span class="gradient-dropdown-arrow-down"></span>
            </span>
            <ul
              class="mce-gradient-type-dropdown-list"
              v-show="showTypesDropdown"
            >
              <li
                v-for="(tmpType, key) in gradientTypes"
                :key="key"
                @click="changeType(tmpType.id)"
                :class="{ active: currentGradientType.id === tmpType.id }"
              >
                <div
                  class="mce-gradient-type-preview"
                  v-bind:style="{ background: tmpType.previewStyle }"
                ></div>
                {{ tmpType.title }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div>
        <div class="mce-gradient-row-title">Start</div>
        <div class="mce-gradient-row-content">
          <color-palette
            :colors-palette="colorsPalette"
            :color-key="0"
            :color="gradientModel.gradientColors[0].color"
            @color-changed="colorChanged"
          ></color-palette>
        </div>
        <div class="mce-gradient-row-title">End</div>
        <div class="mce-gradient-row-content">
          <color-palette
            :colors-palette="colorsPalette"
            :color-key="1"
            :color="gradientModel.gradientColors[1].color"
            @color-changed="colorChanged"
          ></color-palette>
        </div>
      </div>
      <div>
        <div class="mce-gradient-row-title">Level</div>
        <div class="mce-gradient-row-content">
          <div :style="{ width: '160px' }">
            <input
              type="range"
              multiple
              v-for="(tmpColor, key) in gradientModel.gradientColors"
              :key="key"
              @mousedown="currentColorPositionChanging = key"
              @mouseup="currentColorPositionChanging = null"
              @change="canSavePreset = true"
              v-model.number="tmpColor.position"
              number
              min="0"
              step="1"
              max="100"
            />
          </div>
        </div>
      </div>
      <div
        @click="savePreset"
        class="mce-gradient-save-btn"
        :class="{ 'wt-disabled': !canSavePreset }"
      >
        Set as preset
      </div>
    </div>
  </div>
</template>

<script>
/**
 * MceGradientComponent
 * used inside tinmce's box background color plugin
 */
import app from 'app';
import ColorPaletteComponent from 'AssetEditor/functionality/TextOverlay/MceColorPaletteComponent.vue';

export default app.namespace.Vue.MceGradientComponent = app.Vue.extend({
  components: {
    'color-palette': ColorPaletteComponent,
  },

  data: function () {
    return {
      showTypesDropdown: true,
      currentColorPositionChanging: null,
      colorsPalette: [],
      canSavePreset: false,
      lastStashFetch: Date.now(),
      gradientTypes: [
        {
          id: 'topDown',
          title: 'Top - Down',
          angle: 180,
          gradientType: 'LINEAR',
          previewStyle: 'linear-gradient(180deg, #000000 0%, #FFFFFF 100%)',
        },
        {
          id: 'bottomUp',
          title: 'Bottom - Up',
          angle: 0,
          gradientType: 'LINEAR',
          previewStyle: 'linear-gradient(0, #000000 0%, #FFFFFF 100%)',
        },
        {
          id: 'fromLeft',
          title: 'Left to Right',
          angle: 90,
          gradientType: 'LINEAR',
          previewStyle: 'linear-gradient(90deg, #000000 0%, #FFFFFF 100%)',
        },
        {
          id: 'fromRight',
          title: 'Right to Left',
          angle: 270,
          gradientType: 'LINEAR',
          previewStyle: 'linear-gradient(270deg, #000000 0%, #FFFFFF 100%)',
        },
        {
          id: 'radialIn',
          title: 'Radial',
          angle: 0,
          gradientType: 'RADIAL',
          previewStyle:
            'radial-gradient(ellipse at center, #000000 0%, #FFFFFF 100%)',
        },
        // {
        //     id: 'radialOut',
        //     title: 'Radial Out',
        //     angle: 1,
        //     gradientType: 'RADIAL',
        //     previewStyle: 'radial-gradient(ellipse at center, #FFFFFF 0%, #000000 100%)'
        // }
      ],
      gradientModel: {
        gradientType: 'LINEAR',
        angle: 0,
        gradientColors: [
          {
            color: 'rgba(0, 0, 0, 1)',
            position: 0,
          },
          {
            color: 'rgba(0, 0, 0, 0)',
            position: 100,
          },
        ],
      },
      demoPresets: [
        {
          gradientType: 'LINEAR',
          angle: 180,
          gradientColors: [
            {
              color: 'rgba(0, 0, 0, 1)',
              position: 0,
            },
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 100,
            },
          ],
        },
        {
          gradientType: 'LINEAR',
          angle: 0,
          gradientColors: [
            {
              color: 'rgba(0, 0, 0, 1)',
              position: 0,
            },
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 100,
            },
          ],
        },
        {
          gradientType: 'LINEAR',
          angle: 90,
          gradientColors: [
            {
              color: 'rgba(0, 0, 0, 1)',
              position: 0,
            },
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 100,
            },
          ],
        },
        {
          gradientType: 'LINEAR',
          angle: 270,
          gradientColors: [
            {
              color: 'rgba(0, 0, 0, 1)',
              position: 0,
            },
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 100,
            },
          ],
        },
        {
          gradientType: 'RADIAL',
          angle: 0,
          gradientColors: [
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 0,
            },
            {
              color: 'rgba(1, 1, 1, 1)',
              position: 100,
            },
          ],
        },
        {
          gradientType: 'LINEAR',
          angle: 180,
          gradientColors: [
            {
              color: 'rgba(0, 0, 0, 0.5)',
              position: 0,
            },
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 100,
            },
          ],
        },
        {
          gradientType: 'LINEAR',
          angle: 0,
          gradientColors: [
            {
              color: 'rgba(0, 0, 0, 0.5)',
              position: 0,
            },
            {
              color: 'rgba(255, 255, 255, 0)',
              position: 100,
            },
          ],
        },
      ],
    };
  },

  methods: {
    typesDropdownClickedOutside() {
      if (this.showTypesDropdown) {
        this.showTypesDropdown = false;
      }
    },
    onShow() {
      const presetControls = this.$refs.presetControl || [];
      presetControls.forEach((presetControl) => {
        if (presetControl.classList.contains('wt-highlighted')) {
          this.$refs.presetContainer.scrollTop = presetControl.offsetTop;
        }
      });
    },
    changeType(newTypeId) {
      const type = this.gradientTypes.find((tmpType) => {
        return tmpType.id === newTypeId;
      });
      if (type && this.currentGradientType.id != type.id) {
        this.gradientModel.gradientType = type.gradientType;
        this.gradientModel.angle = type.angle;
        this.canSavePreset = true;
      }
      this.showTypesDropdown = false;
    },
    applyPreset(tmpGradientModel) {
      this.canSavePreset = false;
      this.gradientModel = _.cloneDeep(tmpGradientModel);
    },
    gradientModelToCss(gradientModel) {
      return app.namespace.Util.gradientModelToCss(gradientModel);
    },
    savePreset() {
      if (!this.canSavePreset) {
        return;
      }
      const stashPresets = _.isArray(app.stash.get('gradientPresets'))
        ? app.stash.get('gradientPresets').concat()
        : [];
      stashPresets.unshift(_.cloneDeep(this.gradientModel));
      app.stash.set('gradientPresets', stashPresets);
      this.lastStashFetch = Date.now(); // recalculate computed presets based on localstorage :\
      // this.$recompute('presets');
      this.canSavePreset = false;
      this.$refs.presetContainer.scrollTop = 0;
    },
    colorChanged(colorProperties = { colorKey: 0, color: '' }) {
      this.gradientModel.gradientColors[colorProperties.colorKey].color =
        colorProperties.color;
      this.canSavePreset = true;
    },
  },

  computed: {
    currentGradientType() {
      const currentType = this.gradientTypes.find((tmpGradType) => {
        return (
          tmpGradType.angle == this.gradientModel.angle &&
          tmpGradType.gradientType == this.gradientModel.gradientType
        );
      });
      return currentType || {};
    },
    vizModel() {
      const vizModel = _.cloneDeep(this.gradientModel);
      return vizModel;
    },
    presets() {
      this.lastStashFetch; // this is why vuejs sucks, don't remove this line or computed won't update
      let presets = [];
      const stashPresets = _.isArray(app.stash.get('gradientPresets'))
        ? app.stash.get('gradientPresets')
        : [];
      const presetTemplate = {
        gradientType: 'LINEAR',
        angle: 180,
        gradientColors: [
          {
            color: '',
            position: 0,
          },
          {
            color: 'rgba(255, 255, 255, 0)',
            position: 100,
          },
        ],
      };

      stashPresets.forEach((stashedPreset) => {
        presets.push(stashedPreset);
      });

      this.customColors.forEach((tmpCustomColor) => {
        if (tmpCustomColor === '') {
          return;
        }
        const cloneTemplate = _.cloneDeep(presetTemplate);
        const color = tinycolor(tmpCustomColor).toHexString();
        const alpha = tinycolor(tmpCustomColor).getAlpha();
        cloneTemplate.gradientColors[0].color = app.namespace.Util.hexToRgba(
          color,
          alpha
        );
        presets.push(cloneTemplate);
      });

      presets = presets.concat(this.demoPresets);
      return presets;
    },
    customColors() {
      return window.localStorage.customBoxColors
        ? JSON.parse(window.localStorage.customBoxColors)
        : [];
    },
  },

  watch: {
    'gradientModel.gradientColors': {
      handler(newVal) {
        if (_.isNull(this.currentColorPositionChanging)) {
          return;
        }
        // prevent colors position overlap
        const k = this.currentColorPositionChanging;
        const draggableCircleWidth = 10;
        const v = newVal[k];
        const prevPosition = k > 0 ? Number(newVal[k - 1].position) : false;
        const nextPosition =
          k < newVal.length - 1 ? Number(newVal[k + 1].position) : false;
        const position = Number(v.position);

        if (
          _.isNumber(prevPosition) &&
          position < prevPosition + draggableCircleWidth
        ) {
          v.position = prevPosition + draggableCircleWidth;
        }
        if (
          _.isNumber(nextPosition) &&
          position > nextPosition - draggableCircleWidth
        ) {
          v.position = nextPosition - draggableCircleWidth;
        }
      },
      deep: true,
    },
    vizModel: {
      handler(newVal) {
        this.$emit('gradient-selected', newVal);
      },
      deep: true,
    },
  },
});
</script>
